import type { ReactElement } from 'react';
import { AlertTriangle, Shield, Sparkles, Activity } from 'lucide-react';
import type { ShadowAiOrgStats } from '@kirin/api-client/contracts/shadow-ai';
import { MetricCard } from '@/components/ui/metric-card';

/**
 * Tool counter widgets shown at the top of the Shadow AI → Tools page. Network
 * Discovery has its own counters (`NetworkTrafficDashboard`) on its own page, so
 * this component intentionally covers only the tool metrics.
 */
export function OrgSummaryStats({
  stats,
  isLoading,
}: {
  stats: ShadowAiOrgStats | undefined;
  isLoading: boolean;
}): ReactElement {
  const columnCount = 4;

  if (isLoading || !stats) {
    return (
      <div
        className="grid gap-5"
        style={{
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        }}
      >
        {[...Array(columnCount)].map((_, i) => (
          <div
            key={i}
            className="rounded-lg p-5 animate-pulse bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/40"
          >
            <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-700/50" />
            <div className="h-8 w-12 rounded bg-slate-100 dark:bg-slate-700/50 mt-3" />
            <div className="h-4 w-24 rounded bg-slate-100 dark:bg-slate-700/50 mt-1" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className="grid gap-5"
      style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
    >
      <MetricCard
        variant="danger"
        icon={AlertTriangle}
        label="High Risk"
        value={stats.highRiskCount}
        muteWhenZero
      />
      <MetricCard
        variant="neutral"
        icon={Shield}
        label="Root Processes"
        value={stats.rootProcessCount}
        muteWhenZero
      />
      <MetricCard
        variant="info"
        icon={Sparkles}
        label="New This Week"
        value={stats.newThisWeekCount}
      />
      <MetricCard
        variant="info"
        icon={Activity}
        label="Active Now"
        value={stats.activeProcesses}
      />
    </div>
  );
}
